import {Fragment, useState} from 'react';
import { Link, useLocation, useNavigate } from "react-router-dom";
import { cciLogo } from '../helpers/images';
import { Popover, Transition } from '@headlessui/react';
import {
  CalendarIcon,
  Bars3Icon,
  XMarkIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import Button from './Button';
import { classNames, fixUrl } from '../helpers';
import { useAuth } from "../pages/Auth/AuthProvider";
import Modal from "./Modal";
import TextInput from "./inputs/TextInput";
// import {useLazyQuery} from "@apollo/client";
// import {SEARCH_BANNERS} from "../server/graphql/queries";
import {format} from "date-fns";
import Loading from "./Loading";
import banners_prod from "../server/data/banners_prod";

const DEFAULT_PATHS = [
  { id: 'events', name: 'EVENTS', path: '/events', icon: CalendarIcon },
  // { id: 'my-dp-banner', name: 'MY DP BANNERS', path: '/banners', icon: UserGroupIcon }, // hide for now.
];

const AuthButtons = ({ user, logout, mobile }) => {
  if (user) {
    return mobile ? (
      <Popover.Button
        as={'button'}
        onClick={logout}
        className="p-3 flex justify-center rounded-md bg-gray-100 text-base font-medium text-gray-700 w-full">
        Log Out
      </Popover.Button>
    ) : (
      <Button text="Log Out" onClick={logout} type="secondary" />
    )
  } else {
    return null;
    // TODO : NOTE : return null for now.
    // return mobile ? (
    //   <Popover.Button
    //     as={Link}
    //     to="/login"
    //     className="-m-3 mt-5 p-3 flex justify-center rounded-md bg-artfulRed text-base font-medium text-white">
    //     Log In
    //   </Popover.Button>
    // ) : (
    //   <Button text="Log In" icon="right-arrow" to="/login" />
    // )
  }
};

const Navbar = ({ type }) => {
  const { pathname } = useLocation();
  const auth = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [data, setData] = useState({ searchBanners: [] })


  // TODO re-enable this when the backend is back up.
  // const [searchBanners, { loading, data, error }] = useLazyQuery(SEARCH_BANNERS);
  // const performSearch = (text) => searchBanners({ variables: { query: text } });

  const loading = false;
  const performSearchFromLocalData = (text) => {
    let results = [];
    banners_prod.forEach((banner) => {
      if (banner.name.toLowerCase().includes(text.toLowerCase())) {
        results.push(banner)
      }
    })
    setData({ searchBanners: results })
  };

  const searchModalClose = () => {
    setSearchText('');
    setSearch(false);
  };

  const handleOnChange = (e) => {
    const { value } = e.target;

    setSearchText(value);
    setTimeout(() => performSearchFromLocalData(value), 1500);
  };

  const logout = () => auth.logout(() => navigate("/"));

  const navItems = type === 'dashboard' ? [] : DEFAULT_PATHS;

  return (
    <Popover className={classNames(type === 'dashboard' ? 'sticky top-0 z-50' : 'relative', 'bg-white')}>
      <div className="w-full px-4 sm:px-6">
        <div className="flex justify-between items-center border-b-2 border-gray-100 py-4 md:justify-start md:space-x-10">
          <div className="flex justify-start lg:w-0 lg:flex-1">
            <Link to="/">
              <span className="sr-only">Celebration Church International</span>
              <img
                className="h-10 w-auto md:h-12"
                src={cciLogo}
                alt=""
              />
            </Link>
          </div>

          <div className="-mr-2 -my-2 md:hidden flex items-center">
            { type !== 'dashboard' && <Button text="" type="secondary" icon="search" onClick={() => setSearch(true)} /> }
            { auth.user && <img className="h-8 w-8 rounded-full" src={auth.user?.image} alt="" /> }
            <Popover.Button className={classNames(
              'bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500',
              'hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-artfulRed'
            )}>
              <span className="sr-only">Open menu</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </Popover.Button>
          </div>

          <Popover.Group as="nav" className="hidden md:flex space-x-10">
            {
              navItems.map(item => (
                <Link
                  to={item.path}
                  key={item.id}
                  className={classNames(
                    pathname === item.path ? 'underline text-artfulRed font-bold' : 'text-gray-600',
                    'text-sm hover:text-artfulRed hover:underline underline-offset-4'
                  )}>
                  { item.name }
                </Link>
              ))
            }
          </Popover.Group>

          <div className="hidden md:flex items-center justify-end md:flex-1 lg:w-0">
            <Modal
              open={search}
              onClose={searchModalClose}
              title="Search Events"
              subtitle="type to search"
              icon={MagnifyingGlassIcon}
              ctaBtns={{
                secondary: { text: 'Close', onClick: searchModalClose }
              }}>
              <>
                <div className="mt-8">
                  <TextInput
                    id="search"
                    name="search"
                    value={searchText}
                    onChange={handleOnChange}
                  />
                </div>

                <div>
                  {loading && <Loading loading={loading} />}
                  {data?.searchBanners && data?.searchBanners.map((event) => (
                    <div
                      key={event.id}
                      onClick={() => {
                        searchModalClose();
                        navigate(`/${event.url}`);
                      }}
                      className="my-4 py-2 hover:bg-gray-100 rounded-md cursor-pointer transition-all">
                      <dt>
                        <div className="absolute flex h-12 w-12 items-center justify-center rounded-md bg-gray-400 text-white">
                          <img className="h-full w-full rounded-md" src={fixUrl(event.imageUrl)} alt="img" />
                        </div>
                        <p className="ml-16 text-lg font-medium leading-6 text-gray-900">{event.name}</p>
                      </dt>
                      <dd className="mt-2 ml-16 text-sm text-gray-500">{format(new Date(event.eventDate), 'yyyy-MM-dd')}</dd>
                    </div>
                  ))}
                </div>
              </>
            </Modal>
            { type !== 'dashboard' && <Button text="" type="secondary" icon="search" onClick={() => setSearch(true)} /> }
            { auth.user && <img className="h-8 w-8 rounded-full mx-3" src={auth.user?.image} alt="" /> }
            <AuthButtons user={auth.user} logout={logout} />
          </div>
        </div>
      </div>

      <Transition
        as={Fragment}
        enter="duration-200 ease-out"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="duration-100 ease-in"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95">
        <Popover.Panel focus className="absolute z-50 top-0 inset-x-0 p-2 transition transform origin-top-right md:hidden">
          <div className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 bg-white divide-y-2 divide-gray-50">
            <div className="pt-5 pb-6 px-5">
              <div className="flex items-center justify-between">
                <div>
                  <img
                    className="h-10 w-auto md:h-12"
                    src={cciLogo}
                    alt=""
                  />
                </div>
                <div className="-mr-2">
                  <Popover.Button className={classNames(
                    'bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400',
                    'hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset',
                    'focus:ring-artfulRed'
                  )}>
                    <span className="sr-only">Close menu</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </Popover.Button>
                </div>
              </div>
              <div className="mt-6">
                <nav className="grid gap-y-8">
                  {
                    navItems.map(item => (
                      <Popover.Button
                        as={Link}
                        to={item.path}
                        key={item.id}
                        className={classNames(
                          '-m-3 p-3 flex items-center rounded-md',
                          pathname === item.path ? 'bg-artfulRed font-bold' : ''
                        )}>
                        <item.icon className={classNames(
                          pathname === item.path ? 'text-white' : 'text-gray-500',
                          'flex-shrink-0 h-6 w-6')} aria-hidden="true" />
                        <span className={classNames(
                          pathname === item.path ? 'text-white' : 'text-gray-600',
                          'ml-3 text-sm font-medium'
                        )}>
                          {item.name}
                        </span>
                      </Popover.Button>
                    ))
                  }
                </nav>
              </div>
            </div>
            <div className="p-4 space-y-6">
              <div>
                {/*{type !== 'dashboard' && <Popover.Button*/}
                {/*  as={'button'}*/}
                {/*  onClick={() => setSearch(true)}*/}
                {/*  className="p-3 flex items-center rounded-md w-full">*/}
                {/*  <MagnifyingGlassIcon className="text-gray-500 flex-shrink-0 h-6 w-6" aria-hidden="true"/>*/}
                {/*  <span className="text-gray-700 ml-3 text-base font-medium">*/}
                {/*    Search*/}
                {/*  </span>*/}
                {/*</Popover.Button>}*/}
                <AuthButtons user={auth.user} logout={logout} mobile={true} />
              </div>
            </div>
          </div>
        </Popover.Panel>
      </Transition>
    </Popover>
  )
};

Navbar.defaultProps = {
  type: 'default',
};

export default Navbar;
